'use client'

import { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { mq, vw, getTransition } from '@/styles'
import { usePageDesign, useTranslator } from '@/hooks'
import { Select } from './commons/inputs'

interface IWhereToBuyFilter {
  data: { countries: { name: string, value: string }[] },
  selectedCountry: string
  setCountry: Dispatch<SetStateAction<string>>
  selectedCity: string
  setCity: Dispatch<SetStateAction<string>>
  filterResults: number,
  online?: boolean
}


const getAllCities = countries => {
  const allCities = []

  countries.map(country => {
    const formattedCities = country.cities.map(city => ({
      name: city,
      value: city
    }))
    allCities.push(...formattedCities)
  })

  return allCities
}

const getCitiesByCountry = (selectedCountry, countries) => {
  const allCitiesByCountry = []

  countries.map(country => {
    if (country.name === selectedCountry) {
      const formattedCities = country.cities.map(city => ({
        name: city,
        value: city
      }))
      allCitiesByCountry.push(...formattedCities)
    }
  })

  return allCitiesByCountry
}

export const WhereToBuyFilter = memo<IWhereToBuyFilter>(({ data, setCountry, selectedCountry, setCity, selectedCity, filterResults, online = false }) => {
  const { t } = useTranslator()
  const countryFilter = useMemo(() => data.countries, [data.countries])
  const cityFilter = useMemo(() =>  !online && (selectedCountry === 'Country' ? getAllCities(data.countries) : getCitiesByCountry(selectedCountry, data.countries)) , [online, selectedCountry, data.countries])
  const { setHeaderTextColor, setPageHasHeaderBgImage } = usePageDesign()

  const handleCountrySelect =useCallback((value) => {
    setCountry(value)
    setCity('City')
  }, [setCountry, setCity])

  const handleCitySelect = useCallback((value) => {
    setCity(value)
  }, [setCity])

  useEffect(() => {
    setHeaderTextColor('dark')
    setPageHasHeaderBgImage(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  

  return (
    <Wrapper variants={imageVariants} initial='initial' exit='exit' animate='animate' $online={online}>
      <Select options={countryFilter} key={selectedCountry} name='country' defaultValue={selectedCountry} onChange={handleCountrySelect} $light={true} placeholder={selectedCountry}/>
      {!online && <Select options={cityFilter} key={selectedCity} name='city' defaultValue={selectedCity} onChange={handleCitySelect} $light={true} placeholder={selectedCity}/> }
      <Result>
        <div>{t('project.wheretobuy_page.results')}</div>
        {(selectedCountry !== 'Country' || selectedCity !== 'City') && <div className='filter-results'>
          {filterResults} {!online && t('project.wheretobuy_page.distributors')}{online && t('project.wheretobuy_page.online')}
        </div>}
      </Result>
    </Wrapper>
  )
})



const Wrapper = styled(motion.div)<{ $online: boolean }>`
  background-color: var(--color-background);
  display: grid;
  grid-area: 2 / 1 / span 1 / span 6;
  grid-column-gap: ${vw(23, 'mobile')};
  grid-template-columns: ${({ $online }) => $online ? '1fr' : '1fr 1fr'};
  grid-template-columns: 1fr 1fr;
  margin: 0 0 ${vw(28, 'mobile')};
  opacity: 0;
  position: sticky;
  top: ${vw(114, 'mobile')};
  z-index: 10;

  ${mq.greaterThan('tablet')}{
    grid-area: 2 / 1 / span 1 / span 3;
    margin: 0 0 0 ${vw(40, 'desktop')};
    display: block;
    padding: 0;
    position: static;
  }

  + .variant-info {
    margin-top: ${vw(16, 'mobile')};

    ${mq.greaterThan('tablet')}{
      margin-top: 0;
    }
  }

  > div {
    margin-bottom: ${vw(60, 'desktop')};

    ${mq.greaterThan('tablet')}{
      margin-bottom: ${vw(60, 'desktop')};
    }
  }

  .chevron {
    bottom: 20px;
  }
`

const Result = styled.div`
  display: none;

  ${mq.greaterThan('tablet')}{
    display: block;
    margin-top: ${vw(76, 'desktop')};
  }

  .filter-results {
    opacity: .4;
  }
`

const imageVariants = {
  'initial': {
    opacity: 0,
    transition: {
      ...getTransition(),
    }
  },
  'animate': {
    opacity: 1,
    transition: {
      ...getTransition(),
    }
  },
  'exit': {
    opacity: 0,
    transition: {
      ...getTransition(),
    }
  }
}